import React, { useCallback, useEffect, useRef, useState } from "react";
import useSubcontractorRefresh from "../hooks/useSubcontractorRefresh";
import { toast } from "./ui/sonner";
import { Button } from "./ui/button";
import { Award, Eye, Loader2, Mail, Monitor, Phone, Save, Star } from "lucide-react";
import { RfqSendPreviewDialog } from "./RfqSendPreviewDialog";

const Ch = ({ ok, icon: I, title }) => <span title={title} className={`inline-flex items-center ${ok ? "text-emerald-600" : "text-slate-300"}`}><I className="h-3 w-3" /></span>;

/* 草稿态：勾选当前项目同分公司的工种候选 → 保存名单 → 预览发送清单 → 确认发送（发送前不外发任何消息） */
export const RfqDraftPanel = ({ api, rfq, k, zh, canManage, onChanged }) => {
  const [cands, setCands] = useState(null);
  const invitedIds = (rfq.invites || []).map((i) => i.companyId).filter(Boolean);
  const [sel, setSel] = useState(() => new Set(invitedIds));
  const allowedIds = useRef(null);
  const candidateRequest = useRef(0);
  const [candidateReady, setCandidateReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const [loadError, setLoadError] = useState(false);
  // 历史草稿可能预选跨分公司公司。请求发起时立即撤销可保存状态，避免旧勾选在候选尚未核准或刷新失败时进入预览。
  // 成功后剔除不可选 ID，保留有效的未保存勾选；失败只锁住操作，不清空用户的选择。
  const loadCandidates = useCallback(() => {
    const request = ++candidateRequest.current;
    allowedIds.current = null;
    setCandidateReady(false);
    // 已打开的发送清单基于上一版候选；刷新开始即关闭，不能在核验失效期间继续发送。
    setPreview(false);
    // 旧候选即使属于同一 RFQ，也可能在刷新间隙被转到其他分公司；保留勾选供成功后恢复，但不再展示旧名单。
    setCands(null);
    return api.get(`/rfq/${rfq.id}/candidates`).then(({ data }) => {
      if (request !== candidateRequest.current) return;
      const valid = new Set((data.items || []).map((c) => c.companyId));
      allowedIds.current = { rfqId: rfq.id, ids: valid };
      setCands(data);
      setSel((current) => new Set([...current].filter((id) => valid.has(id))));
      setLoadError(false);
      setCandidateReady(true);
    }).catch(() => {
      if (request !== candidateRequest.current) return;
      setLoadError(true);
      setCands(null);
    });
  }, [api, rfq.id]);
  useEffect(() => { loadCandidates(); }, [loadCandidates]);
  useSubcontractorRefresh(loadCandidates);
  const invitedKey = [...invitedIds].sort().join(",");
  // 服务端邀请名单刷新时也遵守最近一次成功取得的项目候选，不能把旧跨分公司 ID 再带回勾选状态。
  useEffect(() => {
    const valid = allowedIds.current?.rfqId === rfq.id ? allowedIds.current.ids : null;
    setSel(new Set((invitedKey ? invitedKey.split(",") : []).filter((id) => !valid || valid.has(id))));
  }, [invitedKey, rfq.id]);
  const canUseCandidates = candidateReady && allowedIds.current?.rfqId === rfq.id;
  const dirty = sel.size !== invitedIds.length || invitedIds.some((id) => !sel.has(id));
  const toggle = (id) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const save = async () => {
    // 按钮禁用之外再守住处理函数，防止加载中的旧事件或其他调用绕过候选校验。
    const valid = allowedIds.current?.rfqId === rfq.id ? allowedIds.current.ids : null;
    if (!candidateReady || !valid) { toast.error(zh ? "请等待当前项目候选名单加载成功后重试" : "Wait for project candidates to load, then retry"); return false; }
    const selectedIds = [...sel].filter((id) => valid.has(id));
    if (!selectedIds.length) { toast.error(zh ? "请至少勾选一家分包商" : "Select at least one company"); return false; }
    setBusy(true);
    try { await api.put(`/rfq/${rfq.id}/invites`, { companyIds: selectedIds }); toast.success(zh ? "邀请名单已保存（尚未发送）" : "Invite list saved (not sent yet)"); onChanged(); setBusy(false); return true; } catch (e) { toast.error(e?.detailText || (zh ? "保存失败" : "Failed")); setBusy(false); return false; }
  };
  const openPreview = async () => {
    if (!canUseCandidates || allowedIds.current?.rfqId !== rfq.id) return;
    const validatedRequest = candidateRequest.current;
    if (dirty && !(await save())) return;
    // 保存请求尚未返回时可能触发候选刷新；即使旧保存成功，也必须重新选择并预览新名单。
    if (validatedRequest !== candidateRequest.current || allowedIds.current?.rfqId !== rfq.id) return;
    if (!sel.size) { toast.error(zh ? "请至少勾选一家分包商" : "Select at least one company"); return; }
    setPreview(true);
  };
  const items = cands?.items || [];
  const rec = new Set(cands?.recommended || []);
  const noVisit = rfq.visitPolicy === "not_required";
  const [vpBusy, setVpBusy] = useState(false);
  const setPolicy = async (v) => {
    setVpBusy(true);
    try { await api.patch(`/rfq/${rfq.id}`, { visitPolicy: v ? "not_required" : "optional" }); toast.success(v ? (zh ? "已设置：本项目不需要踏勘，分包端只显示直接报价" : "No site visit for this RFQ") : (zh ? "已恢复：分包可预约踏勘或直接报价" : "Site visit optional")); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "设置失败" : "Failed")); }
    finally { setVpBusy(false); }
  };
  return (
    <div data-testid={`rfq-draft-${k}`} data-selected={sel.size} className="rounded-lg border border-amber-300 bg-amber-50/60 p-2.5 space-y-2">
      {loadError && <p role="alert" className="text-xs text-red-700">{zh ? "候选名单更新失败，已保留当前选择。" : "Unable to update candidates; your selection is preserved."} <button type="button" onClick={loadCandidates} className="underline min-h-[36px] px-2">{zh ? "重试" : "Retry"}</button></p>}
      <p className="font-semibold text-amber-900">{zh ? "第 1 步 · 选择要邀请的分包商（勾选后先预览发送清单，再确认发送）" : "Step 1 · Choose subcontractors to invite (preview before sending)"}</p>
      <label data-testid={`rfq-visit-policy-${k}`} data-value={rfq.visitPolicy || "optional"} className={`flex items-start gap-2 rounded-md border px-2 py-1.5 cursor-pointer ${noVisit ? "border-sky-300 bg-sky-50" : "border-slate-200 bg-white/70"}`}>
        <input type="checkbox" data-testid={`rfq-visit-policy-check-${k}`} disabled={!canManage || vpBusy} checked={noVisit} onChange={(e) => setPolicy(e.target.checked)} className="mt-0.5 h-3.5 w-3.5 accent-sky-700" />
        <span className="text-slate-700"><b>{zh ? "本项目不需要现场踏勘" : "No site visit needed for this job"}</b><span className="text-slate-500">{zh ? " — 勾选后分包端不显示「预约踏勘」选项，直接根据图纸 / 照片报价；不勾选则踏勘可选（分包可预约踏勘后报价，或直接报价）。踏勘不影响选定授标。" : " — subcontractors quote directly from drawings/photos; otherwise a site visit is optional."}</span></span>
      </label>
      {!canUseCandidates ? <p className="text-slate-500">{loadError ? (zh ? "当前项目候选名单待重新核验，请重试。" : "Project candidates need verification; retry.") : <><Loader2 className="h-3 w-3 animate-spin inline mr-1" />{zh ? "正在核验当前项目候选名单…" : "Verifying project candidates…"}</>}</p>
        : items.length === 0 ? <p data-testid={`rfq-draft-empty-${k}`} className="text-slate-600">{zh ? "当前项目所属分公司暂无该工种可邀请的分包商，请先到「分包商库」为该分公司添加。" : "No eligible subcontractors for this trade in the project's branch. Add one in the directory."}</p> : (
          <ul className="space-y-1" data-testid={`rfq-draft-list-${k}`}>
            {items.map((c) => (
              <li key={c.companyId} data-testid={`rfq-cand-${c.companyId}`} data-checked={sel.has(c.companyId) ? "1" : "0"} className={`flex items-center gap-2 rounded-md border px-2 py-1 bg-white ${sel.has(c.companyId) ? "border-teal-400" : "border-slate-200"}`}>
                <input type="checkbox" data-testid={`rfq-cand-check-${c.companyId}`} disabled={!canManage} checked={sel.has(c.companyId)} onChange={() => toggle(c.companyId)} className="h-3.5 w-3.5 accent-teal-700" />
                <span className="font-semibold text-slate-800">{c.companyName}</span>
                {rec.has(c.companyId) && <span className="inline-flex items-center gap-0.5 px-1 py-px rounded bg-emerald-600 text-white text-[9px] font-semibold"><Award className="h-2.5 w-2.5" />{zh ? "推荐" : "Top"}</span>}
                {c.contact && <span className="text-slate-500">{c.contact}</span>}
                {c.region && <span className="text-slate-400">{c.region}</span>}
                <span className="inline-flex items-center gap-0.5 text-amber-600" title={zh ? "综合评分" : "Score"}><Star className="h-3 w-3 fill-amber-400" />{c.score != null ? Number(c.score).toFixed(1) : "—"}</span>
                <span className="text-slate-500">{zh ? `合作 ${c.projectCount} 项` : `${c.projectCount} projects`}</span>
                <span className="ml-auto inline-flex items-center gap-1.5" data-testid={`rfq-cand-ch-${c.companyId}`} title={zh ? "可达渠道：系统账号 / 短信 / 邮件" : "Channels"}>
                  <Ch ok={c.hasAccount} icon={Monitor} title={zh ? (c.hasAccount ? "有系统账号" : "无系统账号") : "account"} /><Ch ok={c.phones > 0} icon={Phone} title={`${c.phones} ${zh ? "个电话" : "phones"}`} /><Ch ok={c.emails > 0} icon={Mail} title={`${c.emails} ${zh ? "个邮箱" : "emails"}`} />
                  {!c.hasAccount && !c.phones && !c.emails && <span className="text-red-600 font-semibold">{zh ? "无联系方式" : "unreachable"}</span>}
                </span>
              </li>
            ))}
          </ul>
        )}
      {canManage && (
        <div className="flex items-center gap-2 flex-wrap">
          <span data-testid={`rfq-draft-count-${k}`} className="text-slate-700">{zh ? `已勾选 ${sel.size} 家` : `${sel.size} selected`}{dirty && <span className="text-amber-700 ml-1">{zh ? "· 未保存" : "· unsaved"}</span>}</span>
          {dirty && <Button size="sm" variant="outline" data-testid={`rfq-draft-save-${k}`} disabled={busy || !canUseCandidates} onClick={save} className="h-7 text-[11px]"><Save className="h-3 w-3 mr-1" />{zh ? "保存名单" : "Save list"}</Button>}
          <Button size="sm" data-testid={`rfq-draft-preview-${k}`} disabled={busy || !canUseCandidates || !sel.size} onClick={openPreview} className="h-7 text-[11px] bg-teal-700 hover:bg-teal-800 ml-auto">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3 mr-1" />}{zh ? "第 2 步 · 预览发送清单" : "Step 2 · Preview & send"}</Button>
        </div>
      )}
      {preview && canUseCandidates && <RfqSendPreviewDialog api={api} rfq={rfq} zh={zh} onClose={() => setPreview(false)} onSent={() => { setPreview(false); onChanged(); }} />}
    </div>
  );
};
